import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Form, Tabs, Spin, Modal, message, Row, Col, Input, DatePicker } from 'antd';
import dayjs from 'dayjs';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import type { LookupNames } from './components/Tab1BasicInfo';
import Tab2ColorDetails from './components/Tab2ColorDetails';
import Tab3InspectionSpecs from './components/Tab3InspectionSpecs';
import Tab4Notes from './components/Tab4Notes';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { useP201Mode } from './hooks/useP201Mode';
import { useP201Record } from './hooks/useP201Record';
import { useP201Navigation } from './hooks/useP201Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import { SharedActionBar } from '../../components/SharedActionBar';
import type { ActionBarMode } from '../../components/SharedActionBar';
import { lookupApi } from '../../services/lookupApi';
import p501Api from '../../services/p501Api';
import p5m1Api from '../../services/p5m1Api';
import type { FP210, FP211 } from '../../services/p201Api';
import p201Api from '../../services/p201Api';
import {
  BatchAcceptanceBridge,
  pwp201f1AcceptanceManifest,
  pwp501f1AcceptanceManifest,
  pwp5m1f1AcceptanceManifest,
} from '../../features/acceptance';
import './P201Form.css';

const P201Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [form] = Form.useForm();
  const modeCtrl = useP201Mode();
  const recordCtrl = useP201Record();
  const navCtrl = useP201Navigation();
  const perm = useModulePermission('pwp201f1');
  const [details, setDetails] = useState<FP211[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [activeTab, setActiveTab] = useState('1');
  const [showHistory, setShowHistory] = useState(false);
  const [lastMaintenanceResponse, setLastMaintenanceResponse] = useState<unknown>(null);
  const [lastApproveResponse, setLastApproveResponse] = useState<unknown>(null);
  const [lastReverseResponse, setLastReverseResponse] = useState<unknown>(null);

  const emptyLookupNames: LookupNames = {
    department: '', customer: '', employee: '',
    purchaseReceipt: '', purchaseOrder: '', sampleAnalysis: '', remarkTemplate: '',
  };
  const [lookupNames, setLookupNames] = useState<LookupNames>(emptyLookupNames);

  const onLookupNameChange = useCallback((key: keyof LookupNames, name: string) => {
    setLookupNames(prev => ({ ...prev, [key]: name }));
  }, []);

  const resolveAllNames = useCallback(async (rec: FP210) => {
    const entries: [keyof LookupNames, string, string][] = [
      ['department', 'department', rec.fp210_12 || ''],
      ['customer', 'customer', rec.fp210_36 || ''],
      ['employee', 'employee', rec.fp210_31 || ''],
      ['purchaseReceipt', 'purchaseReceipt', rec.fp210_14 || ''],
      ['purchaseOrder', 'purchaseOrder', rec.fp210_30 || ''],
      ['sampleAnalysis', 'sampleAnalysis', rec.fp210_32 || ''],
      ['remarkTemplate', 'remarkTemplate', rec.fp210_20 || ''],
    ];
    const results = await Promise.all(
      entries.map(async ([key, type, code]) => {
        if (!code.trim()) return [key, ''] as [keyof LookupNames, string];
        try {
          const r = await lookupApi.getName(type, code);
          return [key, r.name || ''] as [keyof LookupNames, string];
        } catch {
          return [key, ''] as [keyof LookupNames, string];
        }
      })
    );
    const next = { ...emptyLookupNames };
    for (const [key, name] of results) next[key] = name;
    setLookupNames(next);
  }, []);

  // Populate form from record
  const populateForm = useCallback((rec: FP210 | null) => {
    if (!rec) {
      form.resetFields();
      setDetails([]);
      setLookupNames(emptyLookupNames);
      return;
    }
    form.setFieldsValue({
      ...rec,
      fp210_11: rec.fp210_11 ? dayjs(rec.fp210_11) : null,
    });
    setDetails(rec.details || []);
    setSelectedRow(0);
    resolveAllNames(rec);
  }, [form, resolveAllNames]);

  // Load record by ID
  const loadRecord = useCallback(async (id: string) => {
    const data = await recordCtrl.load(id);
    if (data) {
      setLastMaintenanceResponse(data);
      populateForm(data);
      navCtrl.setCurrentById(id);
    }
  }, [recordCtrl, populateForm, navCtrl]);

  // Load first record on mount (or review record if ?review= is present)
  useEffect(() => {
    if (navCtrl.list.length > 0 && !recordCtrl.record) {
      if (reviewId) {
        loadRecord(reviewId);
      } else {
        const id = navCtrl.goFirst();
        if (id) loadRecord(id);
      }
    }
  }, [navCtrl.list]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation handlers
  const handleNav = useCallback(async (goFn: () => string | null) => {
    const id = goFn();
    if (id) await loadRecord(id);
  }, [loadRecord]);

  // Action handlers
  const handleAdd = useCallback(() => {
    modeCtrl.toAdd();
    form.resetFields();
    form.setFieldsValue({
      fp210_11: dayjs(),
      fp210_17: 1,
      fp210_171: 2,
      fp210_80: 'N',
      fp210_35: 0,
    });
    setDetails([]);
    setSelectedRow(0);
    setActiveTab('1');
    setLookupNames(emptyLookupNames);
  }, [form, modeCtrl]);

  const handleEdit = useCallback(() => {
    modeCtrl.toEdit();
  }, [modeCtrl]);

  const handleCancel = useCallback(() => {
    modeCtrl.toView();
    populateForm(recordCtrl.record);
  }, [modeCtrl, populateForm, recordCtrl.record]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const master: Partial<FP210> = {
        ...values,
        fp210_11: values.fp210_11 ? values.fp210_11.format('YYYY-MM-DD') : null,
      };
      if (recordCtrl.record && modeCtrl.isEdit) {
        master.fp210_01 = recordCtrl.record.fp210_01;
      }

      const result = await recordCtrl.save(master, details, modeCtrl.isAdd);
      if (result) {
        setLastMaintenanceResponse(result);
        modeCtrl.toView();
        await navCtrl.fetchList();
        navCtrl.setCurrentById(result.fp210_01);
      }
    } catch {
      // form validation failed
    }
  }, [form, details, recordCtrl, modeCtrl, navCtrl]);

  const handleDelete = useCallback(async () => {
    if (!recordCtrl.record) return;
    const ok = await recordCtrl.remove(recordCtrl.record.fp210_01);
    if (ok) {
      setLastMaintenanceResponse({ deleted: recordCtrl.record.fp210_01 });
      const data = await navCtrl.fetchList();
      if (data.length > 0) {
        await loadRecord(data[0].fp210_01);
      } else {
        form.resetFields();
        setDetails([]);
      }
    }
  }, [recordCtrl, navCtrl, loadRecord, form]);

  const handleSearch = useCallback(async (kw: string) => {
    const data = await navCtrl.search(kw);
    if (data.length > 0) {
      await loadRecord(data[0].fp210_01);
    } else {
      recordCtrl.clear();
      populateForm(null);
    }
  }, [navCtrl, loadRecord, recordCtrl, populateForm]);

  // ── Approval Handlers ──
  /* Retired: P201 no longer owns submit/approve/reject/writeoff transitions.
  const handleSubmit = useCallback(() => {
    if (!recordCtrl.record) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定送審此送件單「${recordCtrl.record.fp210_01}」？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          await p201Api.submitForApproval(recordCtrl.record!.fp210_01);
          message.success('已送審');
          await loadRecord(recordCtrl.record!.fp210_01);
        } catch {
          message.error('送審失敗');
        }
      },
    });
  }, [recordCtrl.record, loadRecord]);

  const handleApprove = useCallback(() => {
    if (!recordCtrl.record) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定核准此送件單「${recordCtrl.record.fp210_01}」？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          await p201Api.approve(recordCtrl.record!.fp210_01);
          message.success('已核准');
          await loadRecord(recordCtrl.record!.fp210_01);
        } catch {
          message.error('核准失敗');
        }
      },
    });
  }, [recordCtrl.record, loadRecord]);

  const handleReject = useCallback(() => {
    if (!recordCtrl.record) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定退回此送件單「${recordCtrl.record.fp210_01}」？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await p201Api.reject(recordCtrl.record!.fp210_01);
          message.success('已退回');
          await loadRecord(recordCtrl.record!.fp210_01);
        } catch {
          message.error('退回失敗');
        }
      },
    });
  }, [recordCtrl.record, loadRecord]);

  const handleWriteoff = useCallback(() => {
    if (!recordCtrl.record) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${recordCtrl.record.fp210_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await p201Api.writeoff(recordCtrl.record!.fp210_01);
          message.success('沖銷成功');
          await loadRecord(recordCtrl.record!.fp210_01);
        } catch {
          message.error('沖銷失敗');
        }
      },
    });
  }, [recordCtrl.record, loadRecord]);
  */

  // Map P201 mode ('view'|'add'|'edit') to SharedActionBar mode ('browse'|'add'|'edit')
  const sabMode: ActionBarMode = modeCtrl.mode === 'view' ? 'browse' : modeCtrl.mode;
  const statusCode = (recordCtrl.record?.fp210_80 || '').trim();
  const acceptanceRecords = recordCtrl.record ? [recordCtrl.record] : [];
  const acceptanceSelectedKeys = recordCtrl.record ? [recordCtrl.record.fp210_01] : [];
  const queryAcceptanceExact = useCallback((id: string) => p201Api.get(id), []);

  const handleCentralApprove = useCallback(() => {
    if (!recordCtrl.record) return;
    const requestNo = recordCtrl.record.fp210_01;
    Modal.confirm({
      title: '核准檢驗申請',
      content: `確認透過 P501 核准 ${requestNo}？`,
      okText: '確認核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await p501Api.approve(requestNo);
          setLastApproveResponse(response);
          message.success('核准完成');
          await loadRecord(requestNo);
        } catch {
          message.error('核准失敗');
        }
      },
    });
  }, [loadRecord, recordCtrl.record]);

  const handleCentralReverse = useCallback(() => {
    if (!recordCtrl.record) return;
    const requestNo = recordCtrl.record.fp210_01;
    Modal.confirm({
      title: '沖銷檢驗申請',
      content: `確認透過 P5M1 沖銷 ${requestNo}？`,
      okText: '確認沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await p5m1Api.reverse(requestNo);
          setLastReverseResponse(response);
          message.success('沖銷完成');
          await loadRecord(requestNo);
        } catch {
          message.error('沖銷失敗');
        }
      },
    });
  }, [loadRecord, recordCtrl.record]);

  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: <Tab1BasicInfo form={form} disabled={!modeCtrl.isEditing} lookupNames={lookupNames} onLookupNameChange={onLookupNameChange} />,
    },
    {
      key: '2',
      label: `檢驗明細 (${details.length})`,
      children: (
        <Tab2ColorDetails
          details={details}
          onChange={setDetails}
          disabled={!modeCtrl.isEditing}
          onSelectRow={setSelectedRow}
          selectedRow={selectedRow}
        />
      ),
    },
    {
      key: '3',
      label: '檢驗規範',
      children: (
        <Tab3InspectionSpecs
          details={details}
          selectedRow={selectedRow}
          onChange={setDetails}
          disabled={!modeCtrl.isEditing}
        />
      ),
    },
    {
      key: '4',
      label: '備註',
      children: <Tab4Notes form={form} disabled={!modeCtrl.isEditing} lookupNames={lookupNames} onLookupNameChange={onLookupNameChange} />,
    },
  ];

  return (
    <div className="p201-form-container">
      <Card title="檢驗送件單 (pwp201f1)" size="small">
        <Spin spinning={recordCtrl.loading}>
          <Form form={form} layout="vertical" size="small">
            {/* Form-level controls (above tabs) */}
            <Row gutter={16} style={{ marginBottom: 8 }}>
              <Col span={6}>
                <Form.Item label="送件單號" name="fp210_01" style={{ marginBottom: 4 }}>
                  <Input disabled placeholder="系統自動產生" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="打單日期" name="fp210_11" style={{ marginBottom: 4 }}
                  rules={[{ required: true, message: '打單日期不可空白' }]}>
                  <DatePicker disabled={!modeCtrl.isEditing} style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              size="small"
            />
          </Form>
        </Spin>

        {/* SharedActionBar */}
        <SharedActionBar
          acceptanceActionIds={{
            list: 'PWP201-ACT-READ',
            create: 'PWP201-ACT-CREATE',
            update: 'PWP201-ACT-UPDATE',
            delete: 'PWP201-ACT-DELETE',
          }}
          mode={sabMode}
          isEditing={modeCtrl.isEditing}
          hasRecord={!!recordCtrl.record}
          currentIdx={navCtrl.currentIndex}
          recordCount={navCtrl.list.length}
          onNavFirst={() => handleNav(navCtrl.goFirst)}
          onNavPrev={() => handleNav(navCtrl.goPrev)}
          onNavNext={() => handleNav(navCtrl.goNext)}
          onNavLast={() => handleNav(navCtrl.goLast)}
          onAdd={handleAdd}
          onModify={handleEdit}
          onDelete={() => {
            if (!recordCtrl.record) return;
            Modal.confirm({
              title: '確認刪除',
              content: `確定要刪除送件單「${recordCtrl.record.fp210_01}」？`,
              okText: '刪除',
              okType: 'danger',
              cancelText: '取消',
              onOk: handleDelete,
            });
          }}
          onSave={handleSave}
          onCancel={handleCancel}
          extraBrowseButtons={statusCode === 'N' ? (
            <Button
              type="primary"
              disabled={!recordCtrl.record || !perm.canApprove}
              onClick={handleCentralApprove}
              data-testid="p201-central-approve"
              data-acceptance-action="PWP501-ACT-APPROVE"
            >
              P501 核准
            </Button>
          ) : statusCode === 'P' ? (
            <Button
              danger
              disabled={!recordCtrl.record || !perm.canApprove}
              onClick={handleCentralReverse}
              data-testid="p201-central-reverse"
              data-acceptance-action="PWP5M1-ACT-REVERSE"
            >
              P5M1 沖銷
            </Button>
          ) : null}
          approval={{
            statusCode,
            isApproved: statusCode === 'P',
            isPending: false,
            isLocked: statusCode === 'P',
          }}
          onExit={() => navigate('/erp')}
          canCreate={perm.canCreate}
          canEdit={perm.canEdit}
          canDelete={perm.canDelete}
          canApprove={perm.canApprove}
        />

        {/* Approval History Modal */}
        <ApprovalHistoryModal
          visible={showHistory}
          orderNo={recordCtrl.record?.fp210_01 || ''}
          onClose={() => setShowHistory(false)}
        />

        <BatchAcceptanceBridge
          manifest={pwp201f1AcceptanceManifest}
          records={acceptanceRecords}
          selectedKeys={acceptanceSelectedKeys}
          keyOf={(item) => item.fp210_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={lastMaintenanceResponse}
          loading={recordCtrl.loading}
          onAutoFill={() => {
            const id = navCtrl.list[0]?.fp210_01;
            if (id) void loadRecord(id);
          }}
          autoFillDisabled={navCtrl.list.length === 0}
          autoFillReason="安全載入第一張既有送檢單，不會新增、修改或送出。"
          screenDraft={{
            mode: modeCtrl.mode,
            master: form.getFieldsValue(),
            details,
          }}
        />

        <div data-acceptance-action="PWP501-ACT-QUERY">
          <BatchAcceptanceBridge
            manifest={pwp501f1AcceptanceManifest}
            records={acceptanceRecords}
            selectedKeys={acceptanceSelectedKeys}
            keyOf={(item) => item.fp210_01}
            queryExact={queryAcceptanceExact}
            lastApiResponse={lastApproveResponse}
            loading={recordCtrl.loading}
            onAutoFill={() => {
              if (recordCtrl.record) void loadRecord(recordCtrl.record.fp210_01);
            }}
            autoFillDisabled={!recordCtrl.record || statusCode !== 'N'}
            fieldCoverageMode="shared_document_action"
            autoFillReason="安全載入目前待核准送檢單，不會自動執行核准。"
          />
        </div>

        <div data-acceptance-action="PWP5M1-ACT-QUERY">
          <BatchAcceptanceBridge
            manifest={pwp5m1f1AcceptanceManifest}
            records={acceptanceRecords}
            selectedKeys={acceptanceSelectedKeys}
            keyOf={(item) => item.fp210_01}
            queryExact={queryAcceptanceExact}
            lastApiResponse={lastReverseResponse}
            loading={recordCtrl.loading}
            onAutoFill={() => {
              if (recordCtrl.record) void loadRecord(recordCtrl.record.fp210_01);
            }}
            autoFillDisabled={!recordCtrl.record || statusCode !== 'P'}
            fieldCoverageMode="shared_document_action"
            autoFillReason="安全載入目前已核准送檢單，不會自動執行反核准。"
          />
        </div>
      </Card>
    </div>
  );
};

export default P201Form;
